import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Supabase redirects with #access_token in the URL hash
    // onAuthStateChange fires with SIGNED_IN / PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setSessionReady(true);
      }
    });
    // Also check existing session in case user was already signed in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setSuccess(true);
    setTimeout(() => navigate('/dashboard'), 2500);
  };

  const pw = password;
  const strength = [
    { label: '6+ chars', ok: pw.length >= 6 },
    { label: 'Uppercase', ok: /[A-Z]/.test(pw) },
    { label: 'Number', ok: /\d/.test(pw) },
  ];

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/d3c3ec87-e7de-4a4f-b250-82f788f182b3.png"
            alt="API MART"
            className="w-14 h-14 rounded-xl mx-auto mb-4 object-cover"
          />
          <h1 className="text-2xl font-bold text-white">API MART</h1>
          <p className="text-white/40 text-sm mt-1">SET NEW PASSWORD</p>
        </div>

        <div className="bg-white rounded-2xl p-6">
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-4"
            >
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-fill text-green-500 text-3xl" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Password updated!</h3>
              <p className="text-xs text-gray-500">Redirecting to your dashboard...</p>
            </motion.div>
          ) : !sessionReady ? (
            <div className="text-center py-8">
              <i className="ri-loader-4-line animate-spin text-brand-500 text-2xl block mb-3" />
              <p className="text-sm text-gray-500">Verifying your reset link...</p>
              <button
                onClick={() => navigate('/forgot-password')}
                className="mt-4 text-xs text-brand-500 hover:underline cursor-pointer"
              >
                Request a new link
              </button>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-lock-password-line text-brand-500 text-2xl" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Set a new password</h2>
                <p className="text-xs text-gray-400 mt-1">Choose a strong password for your account.</p>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5"
                >
                  <i className="ri-error-warning-fill text-red-500" />
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">New Password</label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-400"
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPw ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                  {pw.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      {strength.map(rule => (
                        <span
                          key={rule.label}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors ${rule.ok ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                        >
                          {rule.ok && <i className="ri-check-line mr-0.5" />}{rule.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-400"
                      placeholder="••••••••"
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />Updating...
                    </span>
                  ) : 'Update Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
